import React, { useState, useEffect, useCallback } from 'react';
import MobileBottomNav from '../components/layout/MobileBottomNav';
import "./LandingPage.css";
import CommonNavbar from "../components/CommonNavbar";
import { AUTH_STORAGE_KEY } from "../socket";
import { useNavigate } from "react-router-dom";
import AdvertisementSlider from "../components/AdvertisementSlider";
import CategoryGroupSection from "../components/CategoryGroupSection";
import LatestBlogsSection from "../components/LatestBlogsSection";
import LatestVideosSection from "../components/LatestVideosSection";
import FloatingSideButtons from "../components/FloatingSideButtons";
import FooterPage from "../components/Footer";

// ── Icon mapping for categories ────────────────────────────────────────────────
const CATEGORY_ICONS = {
  // Home Services
  plumber: "💧",
  plumbing: "💧",
  electrician: "⚡",
  electrical: "⚡",
  carpenter: "🪵",
  carpentry: "🪵",
  painter: "🎨",
  painting: "🎨",
  pest: "🐜",
  "pest control": "🐜",
  cleaning: "🧹",
  housekeeping: "🧹",

  // AC & Appliances
  ac: "❄️",
  "ac repair": "❄️",
  hvac: "❄️",
  appliance: "📦",
  refrigerator: "🧊",

  // Beauty & Wellness
  salon: "💇",
  salons: "💇",
  "beauty parlour": "💄",
  "beauty parlours": "💄",
  beauty: "💄",
  spa: "🧖",
  massage: "🧖",
  wellness: "🧖",
  gym: "🏋️",
  gyms: "🏋️",
  fitness: "🏋️",
  yoga: "🧘",
  "yoga classes": "🧘",

  // Medical
  hospital: "🏥",
  hospitals: "🏥",
  dentist: "🦷",
  dentists: "🦷",
  cardiologist: "❤️",
  cardiologists: "❤️",
  neurologist: "🧠",
  neurologists: "🧠",
  dermatologist: "🩺",
  dermatologists: "🩺",
  gynaecologist: "👩‍⚕️",
  gynaecologists: "👩‍⚕️",
  ent: "👂",
  sexologist: "🩺",
  diagnostic: "🔬",

  // Education
  school: "🏫",
  schools: "🏫",
  college: "🎓",
  colleges: "🎓",
  coaching: "📚",
  tutor: "📚",
  training: "📚",
  institute: "📖",

  // Restaurants & Food
  restaurant: "🍽️",
  restaurants: "🍽️",
  cafe: "☕",
  cafes: "☕",
  bakery: "🥐",
  bakeries: "🥐",
  caterer: "🍱",
  caterers: "🍱",
  fastfood: "🍔",
  "fast food": "🍔",
  hotel: "🏨",
  hotels: "🏨",

  // Travel
  travel: "✈️",
  "travel agents": "✈️",
  tour: "🧳",
  "tour operators": "🧳",
  visa: "🛂",
  resort: "🏖️",
  resorts: "🏖️",

  // Automotive
  car: "🚗",
  cars: "🚗",
  "car dealer": "🚗",
  "car dealers": "🚗",
  "car rental": "🚘",
  "car rentals": "🚘",
  mechanic: "🔧",
  bike: "🏍️",
  "bike dealer": "🏍️",
  tyre: "🛞",
  "service centre": "🔧",

  // Technology
  website: "🌐",
  "website design": "🌐",
  software: "💻",
  computer: "💻",
  android: "📱",
  app: "📱",
  developer: "👨‍💻",
  cctv: "📹",
  security: "🛡️",
  "digital marketing": "📈",

  // Business & Professional
  architect: "📐",
  auditor: "📊",
  accountant: "🧮",
  "chartered accountant": "🧮",
  ca: "🧮",
  advocate: "⚖️",
  lawyer: "⚖️",
  legal: "⚖️",
  consultant: "💼",
  tax: "💰",

  // Real Estate
  property: "🏢",
  builder: "🏗️",
  builders: "🏗️",
  contractor: "🏗️",
  estate: "🏠",
  realtor: "🏠",
  commercial: "🏬",

  // Shopping
  jewellery: "💍",
  clothing: "👕",
  furniture: "🛋️",
  mobile: "📱",
  book: "📚",
  mall: "🛍️",

  // Events
  wedding: "💒",
  photographer: "📸",
  photography: "📸",
  planner: "🎉",
  event: "🎊",
  banquet: "🏛️",

  // Sports
  sports: "⚽",
  club: "🏆",
  swimming: "🏊",
  martial: "🥋",

  // Advertising & Marketing
  advertising: "📢",
  newspaper: "📰",
  marketing: "📈",

  // Agriculture
  agri: "🌾",
  agriculture: "🌾",
  farmer: "🌱"
};

function getIconForCategory(name) {
  const key = String(name || "").trim().toLowerCase();
  
  // Direct match first (exact or substring)
  for (const [pattern, icon] of Object.entries(CATEGORY_ICONS)) {
    if (key === pattern || key.includes(pattern)) {
      return icon;
    }
  }
  
  // Fallback: return default icon
  return "🔧";
}




// ── Categories ────────────────────────────────────────────────────────────────
function Categories({ onFilter, categories = [] }) {
  const [categorySearch, setCategorySearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [displayCount, setDisplayCount] = useState(14);

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(categorySearch.toLowerCase())
  );


  const visibleCategories = filteredCategories.slice(0, displayCount);
  const hasMore = filteredCategories.length > displayCount;

  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleViewMore = () => {
    setDisplayCount(prev => prev + 14);
  };

  return (
    <section className="eb-section">
      <div className="eb-section-head">
        <h2 className="eb-section-title" style={{color:'black !important'}}>What are you looking for?</h2>
        <p className="eb-section-sub">
          Explore our wide range of trusted service categories across India.
        </p>

        {/* Category Search */}
        <div style={{ maxWidth: "450px", margin: "20px auto 0" }}>
          <input
            type="text"
            placeholder="🔍 Search Category..."
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            className="eb-category-search"
          />
        </div>
      </div>

      <div className="eb-cats-grid">
        {visibleCategories.length > 0 ? (
          visibleCategories.map((c) => {
            const isExpanded = expandedCategories[c.id || c.name];
           return (
  <div
    key={c.id || c.name}
    className={`eb-cat-card ${isExpanded ? "expanded" : ""}`}
    onClick={() => onFilter(c.name)}
    style={{ cursor: "pointer" }}
  >
    <span className="eb-cat-icon">
      {c.icon || getIconForCategory(c.name)}
    </span>

    <div className="eb-cat-name">{c.name}</div>
{/* 
    <div className="eb-cat-count">
      {c.serviceCount || 0} providers
    </div> */}

    {/* Description - shown when expanded */}
    {isExpanded && c.description && (
      <div className="eb-cat-desc">{c.description}</div>
    )}

    {/* View More / Show Less */}
    <div className="eb-cat-actions">
      {c.description && (
        <button
          className="eb-cat-toggle-btn"
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click
            toggleExpand(c.id || c.name);
          }}
        >
          {isExpanded ? "Show Less ▲" : "View More ▼"}
        </button>
      )}
    </div>
  </div>
);
          })
        ) : (
          <div style={{ textAlign: "center", width: "100%" }}>
            No category found
          </div>
        )}
      </div>

      {/* View More Button - Bottom Right */}
      {hasMore && (
        <div className="eb-cats-view-more-container">
          <button className="eb-cats-view-more-btn" onClick={handleViewMore}>
            View More Categories →
          </button>
        </div>
      )}
    </section>
  );
}

// ── Auth helpers ──────────────────────────────────────────────────────────────
const isLoggedIn = () => !!localStorage.getItem(AUTH_STORAGE_KEY);

function slugifyBusinessPath(value) {
  return String(value || "business")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    || "business";
}

function StatsBar() {
  const stats = [
    { num: "1,200+", label: "Verified Providers" },
    { num: "48+", label: "Service Categories" },
    { num: "38", label: "Cities Covered" },
    { num: "98%", label: "Customer Satisfaction" },
  ];
  return (
    <div className="eb-stats-bar">
      {stats.map((s) => (
        <div key={s.label} className="eb-stat-item">
          <span className="eb-stat-num">{s.num}</span>
          <span className="eb-stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

const btnStyle = (c1, c2, textColor) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: `linear-gradient(90deg, ${c1} 0%, ${c2} 100%)`,
  color: textColor,
  border: "none",
  borderRadius: 13,
  padding: "8px 12px",
  fontWeight: 600,
  fontSize: 13,
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  cursor: "pointer",
  transition: "all 0.2s ease",
});

// ── ServicesSection ───────────────────────────────────────────────────────────

// ── WhyUs ─────────────────────────────────────────────────────────────────────
function WhyUs() {
  const cards = [
    { icon: "🛡️", title: "Verified Providers", text: "Every service provider goes through a thorough background check and skill verification process." },
    { icon: "⚡", title: "Instant Booking", text: "Book services in seconds. Confirm availability and schedule appointments in real time." },
    { icon: "💬", title: "Live Support", text: "Our support team is available 7 days a week to help resolve any issues quickly." },
    { icon: "🌐", title: "Nationwide Reach", text: "With coverage across 38+ cities, we bring quality services to every corner of India." },
  ];
  return (
    <section className="eb-section eb-why-section" id="why-us">
      <div className="eb-section-head">
        <div className="eb-section-label">Why E-Mail Bharat</div>
        <h2 style={{color:'white'}} className="eb-section-title">Built on Trust &amp; Reliability</h2>
        <p className="eb-section-sub">We make it easy to find, compare, and book services with confidence.</p>
      </div>
      <div className="eb-why-grid">
        {cards.map((c) => (
          <div key={c.title} className="eb-why-card">
            <div className="eb-why-icon">{c.icon}</div>
            <div className="eb-why-title">{c.title}</div>
            <p className="eb-why-text">{c.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────
function CTA() {
  return (
    <section className="eb-cta-section" id="contact">
      <div className="eb-cta-inner">
        <div className="eb-section-label" style={{ textAlign: "center" }}>Get Started Today</div>
        <h2 className="eb-cta-title">Ready to Find the Perfect Service?</h2>
        <p className="eb-cta-text">Join thousands of satisfied customers who trust E-Mail Bharat for their daily service needs.</p>
        <a href="/login" className="eb-cta-btn">
          Register as Provider
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      </div>
    </section>
  );
}

// // ── Footer ────────────────────────────────────────────────────────────────────
// function Footer() {
//   return (
//     <footer className="eb-footer">
//       <span>E-Mail Bharat</span> &copy; {new Date().getFullYear()} &mdash; All rights reserved. Made with ♥ in India.
//     </footer>
//   );
// }

// ── Root Component ────────────────────────────────────────────────────────────
export default function LandingPage({ setVoiceCall = null }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery]       = useState("");
  const [categories, setCategories]         = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState("Overview");

  // Location state
  const [userLocation, setUserLocation]     = useState(null);   // { lat, lng }
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError]   = useState(null);
  const [nearbyRadius, setNearbyRadius]     = useState(10);     // km

  // Fetch service categories
 useEffect(() => {
  const fetchCategories = async () => {
    try {
      const res = await fetch("/api/service-categories");
      const data = await res.json();

      // Categories (used in the top category grid)
      const mappedCategories = (data.categories || []).map((cat) => ({
        id: cat.id,
        name: cat.name,
        icon: getIconForCategory(cat.name),
        description: cat.description || "",
        serviceCount: cat.serviceCount || 0,
      }));

      setCategories(mappedCategories);

      // Groups (used in CategoryGroupSection)
      setCategoryGroups(data.groups || []);

    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setCategories([]);
      setCategoryGroups([]);
    }
  };

  fetchCategories();
}, []);


 useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);




const fetchIpLocation = useCallback(async () => {
  try {
    const res  = await fetch("https://ipapi.co/json/");
    const data = await res.json();

    if (data.latitude && data.longitude) {
      setUserLocation({ lat: data.latitude, lng: data.longitude });
      setLocationLoading(false);
      setLocationError(null);
      document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
    } else {
      throw new Error("No location in response");
    }
  } catch {
    setLocationLoading(false);
    setLocationError("Could not detect your location. Try searching by area name instead.");
  }
}, []);

const handleRequestLocation = useCallback(() => {
  if (!navigator.geolocation) {
    setLocationError("Geolocation is not supported by your browser.");
    return;
  }
  setLocationLoading(true);
  setLocationError(null);

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      setLocationLoading(false);
      document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
    },
    (err) => {
      // GPS failed → try IP-based location as fallback
      fetchIpLocation();
    },
    {
      timeout: 15000,
      maximumAge: 300000,       // accept cached position up to 5 min old
      enableHighAccuracy: false // false = faster, uses network/wifi not GPS
    }
  );
}, []);

  const handleClearLocation = useCallback(() => {
    setUserLocation(null);
    setLocationError(null);
  }, []);
  

  const handleCategoryFilter = (cat) => {
    if (!cat) return;
    const q = new URLSearchParams();
    q.set("category", cat);
    navigate(`/search?${q.toString()}`);
  };

  return (
    <>
     <FloatingSideButtons />
   <CommonNavbar
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      onSearch={setSearchQuery}
      userLocation={userLocation}
      locationLoading={locationLoading}
      locationError={locationError}
      onRequestLocation={handleRequestLocation}
      onClearLocation={handleClearLocation}
      nearbyRadius={nearbyRadius}
    />
      <AdvertisementSlider />
      <Categories onFilter={handleCategoryFilter} categories={categories} />
      <CategoryGroupSection groups={categoryGroups} />
      <LatestBlogsSection />
      <LatestVideosSection />
      <WhyUs />
      <StatsBar />
      <CTA />
      <FooterPage />
      {isMobile && <MobileBottomNav activeTab={activeTab} setActiveTab={setActiveTab} />}

    </>
  );
}